import {
  Box,
  Card,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import React from "react";
import { ItemCharge } from "../../../../../redux/leads/leads.types";
import { CustomCurrencyFieldV3 } from "../../../../../components/formsComponents";
import { MuiNumberField } from "../../../../../components/formsComponents/MuiNumberField";
import { TextFieldWithLabel } from "../../../../../components/TextFieldWithLabel/TextFieldWithLabel";
import {
  formatCurrencyInIndian,
  formatWithCommas,
  IndianPriceFormatter,
} from "../../../../../helpers/formatWithCommas";
import { formatPrice } from "../../../../../utils/formatPrice";
import { DiscountValueField } from "./DiscountValueField";
import { TaxPercentageField } from "./TaxPercentageField";
import { TaxType } from "./TaxTypeSelector";

/** Default charge rows whose name can't be edited or removed. */
const LOCKED_ITEM_NAMES = new Set([
  "Plate Price Quote",
  "Venue Charges",
  "Decoration Charges",
  "Dj Charges",
  "Bar Handling Charges",
  "Room Charges",
  "Other Charges",
]);

function getLineDiscountAmount(item: ItemCharge) {
  const gross = Number(item.amount || 0) * Number(item.count || 0);
  const d = Number(item.discount || 0);
  if (item.discount_mode === "amount") {
    return Math.min(Math.max(d, 0), gross);
  }
  return (gross * Math.min(Math.max(d, 0), 100)) / 100;
}

interface IQuotationItemsEditorProps {
  items: ItemCharge[];
  isMobile?: boolean;
  disabled?: boolean;
  onItemChange: (index: number, key: string, value: any) => void;
  onTaxTypeChange: (index: number, next: TaxType) => void;
  onDelete: (index: number) => void;
  /** Add-charge / package search toolbar rendered below the items */
  footer?: React.ReactNode;
}

/** Item charges of a single event inside the quotation. */
export const QuotationItemsEditor: React.FC<IQuotationItemsEditorProps> = ({
  items,
  isMobile = false,
  disabled = false,
  onItemChange,
  onTaxTypeChange,
  onDelete,
  footer,
}) => {
  const [editNameIndex, setEditNameIndex] = React.useState<number | null>(null);
  const [editDescriptionIndex, setEditDescriptionIndex] = React.useState<
    number | null
  >(null);

  const renderName = (item: ItemCharge, index: number) => {
    if (LOCKED_ITEM_NAMES.has(item.name)) {
      return (
        <Typography fontWeight="bold" sx={{ ml: 0.5, flex: 1 }}>
          {item.name}
        </Typography>
      );
    }
    const isEditing = editNameIndex === index || !item.name;
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flex: 1 }}>
        {isEditing ? (
          isMobile ? (
            <TextField
              size="small"
              fullWidth
              placeholder="Enter service name"
              value={item.name || ""}
              disabled={disabled}
              onChange={(e) => onItemChange(index, "name", e.target.value)}
              // Keep the field open once typing starts on an empty (new) item
              onFocus={() => setEditNameIndex(index)}
              onBlur={() => item.name?.trim() && setEditNameIndex(null)}
              autoFocus={editNameIndex === index}
              sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
            />
          ) : (
            <TextFieldWithLabel
              heading=""
              placeholder="Enter Name"
              sx={{ mb: 1, width: 300, maxWidth: "100%" }}
              type="text"
              id={`items_charges[${index}].name`}
              value={item.name}
              disabled={disabled}
              onChange={(e) => onItemChange(index, "name", e.target.value)}
              onFocus={() => setEditNameIndex(index)}
              onBlur={() => item.name?.trim() && setEditNameIndex(null)}
              autoFocus={editNameIndex === index}
            />
          )
        ) : (
          <Typography
            fontWeight="bold"
            sx={{ ml: 0.5, flex: 1, cursor: "pointer" }}
            onClick={() => !disabled && setEditNameIndex(index)}
          >
            {item.name || "--"}
          </Typography>
        )}
        <IconButton
          size="small"
          color="error"
          disabled={disabled}
          onClick={() => onDelete(index)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const renderDescription = (item: ItemCharge, index: number) => {
    if (editDescriptionIndex === index) {
      return (
        <TextField
          value={item.description || ""}
          onChange={(e) => onItemChange(index, "description", e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Example: Additional service details..."
          onBlur={() => setEditDescriptionIndex(null)}
          autoFocus
          sx={{
            mt: 0.5,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "background.paper",
              borderRadius: 2,
            },
            "& .MuiInputBase-inputMultiline": { resize: "vertical" },
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: disabled ? "default" : "pointer",
          "&:hover": { backgroundColor: "action.hover", borderRadius: 1 },
        }}
        onClick={() => !disabled && setEditDescriptionIndex(index)}
      >
        <Typography
          sx={{
            flex: 1,
            ml: 0.5,
            fontSize: "0.875rem",
            ...(item.description
              ? { color: "text.secondary", fontStyle: "italic" }
              : { color: "primary.main", fontWeight: 500 }),
          }}
        >
          {item.description || "Description"}
        </Typography>
        <DriveFileRenameOutlineOutlinedIcon
          fontSize="small"
          color="primary"
          sx={{ height: "1rem", opacity: 0.7 }}
        />
      </Box>
    );
  };

  const renderDiscount = (item: ItemCharge, index: number, key: string) => (
    <>
      <DiscountValueField
        name={`items_charges-discount-${key}-${index}`}
        mode={item.discount_mode === "amount" ? "amount" : "percentage"}
        value={
          item.discount !== undefined && item.discount !== null
            ? String(item.discount)
            : ""
        }
        disabled={disabled}
        onModeChange={(m) => onItemChange(index, "discount_mode", m)}
        onChange={(v) => onItemChange(index, "discount", v)}
      />
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, fontWeight: 500 }}
      >
        Discount: {formatPrice(getLineDiscountAmount(item))}
      </Typography>
    </>
  );

  const renderTax = (item: ItemCharge, index: number, key: string) => {
    const taxType = (item.tax_type as TaxType) ?? "GST";
    return (
      <>
        <TaxPercentageField
          name={`tax_percentage_${key}_${index}`}
          taxType={taxType}
          percentage={
            item.tax_type === "VAT" ? item.vat_percentage : item.gst_percentage
          }
          disabled={disabled}
          onTaxTypeChange={(t) => onTaxTypeChange(index, t)}
          onPercentageChange={(val) =>
            onItemChange(
              index,
              taxType === "VAT" ? "vat_percentage" : "gst_percentage",
              val,
            )
          }
        />
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, fontWeight: 500 }}
        >
          Tax Amount:{" "}
          {formatPrice(
            item.tax_type === "VAT" ? item.vat_amount : item.gst_amount,
          )}
        </Typography>
      </>
    );
  };

  if (isMobile) {
    return (
      <Box sx={{ mt: 1 }}>
        {items.map((item, index) => (
          <Card
            key={index}
            sx={{
              mb: 1,
              borderRadius: 1.5,
              boxShadow: "none",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                p: 1,
                bgcolor: "primary.light",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
              }}
            >
              {renderName(item, index)}
            </Box>
            <Box sx={{ p: 1.5 }}>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Count
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formatWithCommas(item.count)}
                    disabled={disabled}
                    onChange={(e) =>
                      onItemChange(
                        index,
                        "count",
                        e.target.value.replace(/[^0-9]/g, ""),
                      )
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Price (₹)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formatCurrencyInIndian(item.amount)}
                    disabled={disabled}
                    onChange={(e) =>
                      onItemChange(
                        index,
                        "amount",
                        e.target.value.replace(/[^0-9]/g, ""),
                      )
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Sub Total
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {IndianPriceFormatter(item.sub_total || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Discount
                  </Typography>
                  {renderDiscount(item, index, "mobile")}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Tax
                  </Typography>
                  {renderTax(item, index, "mobile")}
                </Grid>
              </Grid>
              {renderDescription(item, index)}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1,
                  borderTop: "1px dashed",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Effective Total
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  {IndianPriceFormatter(item.total || 0)}
                </Typography>
              </Box>
            </Box>
          </Card>
        ))}
        {footer}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ overflow: "visible" }}>
      <Table
        size="small"
        sx={{
          tableLayout: "fixed",
          width: "100%",
          "& .MuiTableCell-root": { px: 1 },
        }}
      >
        <TableHead sx={(t) => ({ backgroundColor: t.palette.grey[200] })}>
          <TableRow sx={{ "& .MuiTableCell-root": { fontWeight: 600 } }}>
            <TableCell sx={{ width: "28%" }}>Items</TableCell>
            <TableCell sx={{ width: "8%" }}>Count</TableCell>
            <TableCell sx={{ width: "9%" }}>Price</TableCell>
            <TableCell sx={{ width: "12%", textAlign: "right" }}>
              Sub Total
            </TableCell>
            <TableCell sx={{ width: "14%" }}>Discount</TableCell>
            <TableCell sx={{ width: "15%" }}>Tax</TableCell>
            <TableCell sx={{ width: "14%", textAlign: "right" }}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow
              key={index}
              sx={{ "& .MuiTableCell-root": { verticalAlign: "top", pt: 1 } }}
            >
              <TableCell>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", mb: 0.5 }}>
                    {renderName(item, index)}
                  </Box>
                  {renderDescription(item, index)}
                </Box>
              </TableCell>
              <TableCell>
                <MuiNumberField
                  name="count"
                  label=""
                  value={item.count}
                  disabled={disabled}
                  onChange={(e) => onItemChange(index, "count", e.target.value)}
                />
              </TableCell>
              <TableCell>
                <CustomCurrencyFieldV3
                  id="amount"
                  value={item.amount}
                  disabled={disabled}
                  onChange={(v) => onItemChange(index, "amount", v)}
                />
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formatPrice(item.sub_total)}
                </Typography>
              </TableCell>
              <TableCell>{renderDiscount(item, index, "desktop")}</TableCell>
              <TableCell>{renderTax(item, index, "desktop")}</TableCell>
              <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                  {formatPrice(item.total)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {footer && (
            <TableRow>
              <TableCell colSpan={7}>{footer}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
